// @need-translate
import {useCallback, useEffect, useState} from 'react';
import {getEnv} from '../../../common/env/env';
import {requestContext} from '../../../common/requestContext';
import {getPagewebUrl} from '../../../common/shared/scheme/getPagewebUrl.function';
import {PagewebUrl} from '../../../common/shared/scheme/PagewebUrl.class';
import {formatWithCommas} from '../../../common/shared/utils/formatWithCommas';
import {getKageUrl} from '../../../common/shared/utils/getKageUrl';
import {twMerge} from '../../../common/twMerge';
import useTypedSelector from '../../../common/useTypedSelector';
import {StaticImage} from '../../../design-system/atoms/StaticImage/StaticImage';
import {StaticImageType} from '../../../design-system/atoms/StaticImage/StaticImageType.enum';
import {Text} from '../../../design-system/atoms/Text/Text';
import {WelcomeRecommendContentList} from '../../../design-system/organisms/WelcomeRecommendContentList/WelcomeRecommendContentList';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {LOCAL_STORAGE_VISIT_WELCOME_PAGE} from '../../../popup/constants';
import {useWelcomeQuery} from '../../../wrapper/storeWrapper';
import {WelcomeBannerItem} from '../presentational/WelcomeBannerItem';
import {WelcomeEnjoyKakaoTextBox} from '../presentational/WelcomeEnjoyKakaoTextBox';

export function WelcomeContainer() {
  const data = useWelcomeQuery().data?.welcome;
  const {isMobile} = requestContext.requestInfo.display;

  const [webtoonIndex, setWebtoonIndex] = useState<number>(0);
  const [novelIndex, setNovelIndex] = useState<number>(0);

  const onChangeWebtoonIndex = useCallback(
    (currentIndex: number) => {
      // 같은 경우는 상태 업데이트에 의한 것임
      if (webtoonIndex === currentIndex) return;

      aggregateAndSendLog({
        actionName: '작품_스와이프',
        actionType: 'Event',
        customProps: {
          user_action: 'swipe',
          swipe_direction: webtoonIndex > currentIndex ? '좌' : '우',
        },
      });

      setWebtoonIndex(currentIndex);
    },
    [webtoonIndex]
  );

  const onChangNovelIndex = useCallback(
    (currentIndex: number) => {
      // 같은 경우는 상태 업데이트에 의한 것임
      if (novelIndex === currentIndex) return;

      aggregateAndSendLog({
        actionName: '작품_스와이프',
        actionType: 'Event',
        customProps: {
          user_action: 'swipe',
          swipe_direction: novelIndex > currentIndex ? '좌' : '우',
        },
      });

      setNovelIndex(currentIndex);
    },
    [novelIndex]
  );

  const userUid = useTypedSelector(state => state.user.user?.uid);

  useEffect(() => {
    window.localStorage.setItem(
      LOCAL_STORAGE_VISIT_WELCOME_PAGE,
      String(userUid)
    );
  }, [userUid]);

  return (
    <>
      {/* 웰컴 캐시 수령 영역 */}
      <div className="flex w-full flex-col items-center bg-theme-bg-surface-alt">
        <StaticImage
          imageType={StaticImageType.CrmWelcomeStatic}
          className="w-full max-w-360pxr"
        />

        <div className="flex max-w-360pxr flex-col items-center px-32pxr pb-40pxr">
          <Text className="font-large1-bold text-theme-solid-100">{`반가워요. ${data?.welcomeInfo.userNickname}!`}</Text>
          {data?.welcomeInfo.welcomeCash && (
            <Text className="font-medium2-bold mt-8pxr text-center text-theme-solid-60">
              카카오페이지와 처음 만난 기념으로
              <br />
              <Text className="text-theme-solid-100">
                {`웰컴 ${formatWithCommas(data?.welcomeInfo.welcomeCash)}캐시`}
              </Text>
              를 드립니다.
            </Text>
          )}
        </div>
      </div>
      {/* 추천 콘텐츠 영역 */}
      <div
        className={twMerge(
          'flex w-full flex-col',
          isMobile ? 'pb-16pxr' : 'pb-56pxr',
          !isMobile && 'mx-auto max-w-956pxr'
        )}
      >
        <WelcomeEnjoyKakaoTextBox
          title={'카카페 즐기는 방법 1'}
          desc1={'카카오페이지의 100만 독자들이'}
          desc2={'검증한 작품부터 무료로 만나보세요!'}
          className="pb-48pxr pt-56pxr"
        />
        <div className="w-full">
          <div>
            <div className="h-48pxr w-full px-14pxr py-12pxr">
              <Text className="font-large4-bold text-theme-solid-100">
                구독자 Pick 웹툰
              </Text>
            </div>
            <div
              className={
                isMobile ? 'pb-24pxr pt-12pxr' : 'px-24pxr pb-88pxr pt-12pxr'
              }
            >
              <WelcomeRecommendContentList
                isMobile={isMobile}
                contentList={data?.recommendContents.webtoonList ?? []}
                onChangeIndex={onChangeWebtoonIndex}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '웹툰',
                  },
                })}
              />
            </div>
          </div>
          <div>
            <div className="h-48pxr w-full px-14pxr py-12pxr">
              <Text className="font-large4-bold text-theme-solid-100">
                구독자 Pick 웹소설
              </Text>
            </div>
            <div
              className={
                isMobile ? 'pb-24pxr pt-12pxr' : 'px-24pxr pb-88pxr pt-12pxr'
              }
            >
              <WelcomeRecommendContentList
                isMobile={isMobile}
                contentList={data?.recommendContents.novelList ?? []}
                onChangeIndex={onChangNovelIndex}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '웹소설',
                  },
                })}
              />
            </div>
          </div>
        </div>
      </div>
      {/* 모아보기 페이지 영역 */}
      <div
        className={twMerge(
          'flex w-full flex-col items-center pb-48pxr',
          !isMobile && 'mx-auto max-w-926pxr'
        )}
      >
        <div className="h-1pxr w-full bg-theme-transparent-10 bg-clip-content px-15pxr" />
        <WelcomeEnjoyKakaoTextBox
          title={'카카페 즐기는 방법 2'}
          desc1={'카카오페이지를 본격적으로 즐겨볼까요?'}
          desc2={'다양한 작품들의 세계에 빠져보세요.'}
          className="pb-32pxr pt-56pxr"
        />
        <div className={twMerge('w-full', isMobile ? 'px-15pxr' : 'px-120pxr')}>
          {data?.welcomeInfo.welcomeButtons.map((buttonItem, index) => {
            const _url = getPagewebUrl({
              appScheme: buttonItem.scheme,
            });
            let landing_path;
            if (_url instanceof PagewebUrl) {
              landing_path = _url.fullUrl;
            } else {
              landing_path = _url;
            }
            return (
              <div
                key={buttonItem.title}
                {...putTiaraObjectProps({
                  eventMeta: {
                    id: buttonItem.scheme,
                    type: 'scheme',
                    name: buttonItem.title,
                  },
                  click: {
                    layer1: '웰컴버튼',
                    ordnum: String(index + 1),
                    copy: buttonItem.title,
                    imp_id: `welcome_btn_${String(index + 1).padStart(3, '0')}`,
                    imp_provider: 'service',
                  },
                  viewimp_contents: {
                    imp_id: `welcome_btn_${String(index + 1).padStart(3, '0')}`,
                    imp_provider: 'service',
                    imp_type: 'vimp',
                    layer1: '웰컴버튼',
                    id: buttonItem.scheme,
                    type: 'scheme',
                    name: buttonItem.title,
                  },
                  customProps: {
                    landing_path,
                  },
                })}
              >
                <WelcomeBannerItem
                  bannerTitle={buttonItem.title}
                  bannerImgSrc={getKageUrl({
                    kageUrlPrefix: getEnv().kageUrlPrefix,
                    kageId: buttonItem.kageId,
                    resolution: 'th3',
                  })}
                  scheme={buttonItem.scheme}
                  className="mb-6pxr"
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
